import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import LandingPreview from "@/components/LandingPreview";
import { toast } from "sonner";
import { Mail, Copy } from "lucide-react";

export default function PublicLanding({ byPath = false }) {
  const params = useParams();
  const key = byPath ? params.customPath : params.slug;
  const apiPath = byPath ? `/public/p/${key}` : `/public/lm/${key}`;
  const navigate = useNavigate();
  const [lm, setLm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { token, url, message }

  useEffect(() => {
    api.get(apiPath).then((r) => setLm(r.data)).catch(() => setNotFound(true));
  }, [apiPath]);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const payload = { ...data, variant: lm.variant };
      const r = await api.post(`${apiPath}/submit`, payload);
      if (r.data.requires_confirmation) {
        const fullUrl = `${window.location.origin}/confirm/${r.data.confirmation_token}`;
        setConfirmation({
          token: r.data.confirmation_token,
          url: fullUrl,
          message: r.data.message,
        });
        toast.success("Check your email!");
      } else {
        toast.success("Got it! Sending you to your content…");
        const target = byPath ? `/p/${key}/delivery` : `/lm/${r.data.redirect_slug}/delivery`;
        setTimeout(() => navigate(target), 600);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="card-brutal !p-8 text-center max-w-md" data-testid="lm-not-found">
          <h1 className="font-display font-black text-3xl mb-2">404</h1>
          <p className="text-zinc-600">This lead magnet doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  if (!lm) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;

  if (confirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6 dot-grid">
        <div className="card-brutal !p-10 max-w-lg w-full" data-testid="confirmation-panel">
          <div className="w-14 h-14 mx-auto bg-[#FF4F00] border-2 border-black flex items-center justify-center mb-5"><Mail className="text-white" size={28} /></div>
          <h1 className="font-display font-black text-3xl text-center mb-3">Almost there!</h1>
          <p className="text-zinc-600 text-center mb-6">{confirmation.message}</p>
          <div className="border-2 border-black p-4 bg-zinc-50">
            <div className="label-cap mb-2">Direct confirmation link</div>
            <div className="flex items-center gap-2">
              <input readOnly value={confirmation.url} className="input-brutal flex-1 text-xs" data-testid="confirmation-url" />
              <button onClick={() => { navigator.clipboard.writeText(confirmation.url); toast.success("Copied"); }} className="btn-brutal-outline !px-3" data-testid="copy-confirm-btn"><Copy size={14} /></button>
            </div>
            <p className="text-xs text-zinc-500 mt-2">Click the link or paste it into a new tab to confirm and access your content.</p>
          </div>
          <a href={confirmation.url} className="btn-brutal w-full mt-5 text-center" data-testid="confirm-now-btn">Confirm & continue</a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="public-landing-page" data-variant={lm.variant || "none"}>
      <LandingPreview lm={lm} onSubmit={onSubmit} submitting={submitting} />
      <div className="text-center py-4 text-xs text-zinc-400 bg-white">
        Powered by <span className="font-bold">VibeLead</span>
        {lm.variant && <span className="ml-2 px-2 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-500" data-testid="variant-tag">VARIANT {lm.variant}</span>}
      </div>
    </div>
  );
}
